import { api } from '@/api'
import BackLink from '@/components/BackLink'
import Button from '@/components/Button'
import Checkbox from '@/components/Checkbox'
import Container from '@/components/Container'
import ErrorAllText from '@/components/ErrorText/AllErros'
import Input from '@/components/Input'
import Section from '@/components/Section'
import { CountrySelect, Select, SelectItem, StateSelect } from '@/components/Select'
import SelectAge from '@/components/Select/SelectAge'
import SelectGame from '@/components/Select/SelectGame'
import Spinner from '@/components/Spinner/Spinner'
import Title from '@/components/Title'
import { useModal, usePage } from '@/hooks'
import { championshipApplication } from '@/utils/schemesFormik'
import { GxForm } from '@garpix/garpix-web-components-react'
import { useFormik } from 'formik'
import React from 'react'
import style from './styles/champ_app.module.scss'

const ChampionshipApplication = (): React.ReactElement => {
  const Russian = 'Россия'

  const page = usePage()
  const { openModal } = useModal()
  const { object } = page?.init_state
  const { config } = page?.init_state?.global

  const formik = useFormik({
    validationSchema: championshipApplication,
    initialValues: {
      title: '',
      jeka_version: '',
      school_name: '',
      country: 'Россия',
      locality: '',
      region: '',
      class_name: '',
      email: '',
      phone: '',
      stage: '',
      age: '',
      end_time: '',
      show_code: '0',
      apiError: ''
    },
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit (values, { setSubmitting, setFieldError, resetForm }) {
      api.createChampionship(values)
        .then(response => {
          setSubmitting(false)
          resetForm()
          openModal('championshipsSucceeded')
        })
        .catch((error: { response: any }) => {
          if (error.response !== undefined) {
            setFieldError('errorText', error.response.data.errorText)
          }
        })
    }
  })

  const handleCheckbox = async (e): Promise<void> => {
    const checked = e.target.checked
    const name = e.target.name
    await formik.setFieldValue(name, checked === true ? '1' : '0')
  }

  const onSubmit = formik.submitForm

  return (
    <Container>
      <BackLink title='Все чемпионаты' href={config.championships} />
      <Title type='h1' className={style.app__title}>
        {object.title}
      </Title>
      <Section>
        <GxForm onGx-submit={onSubmit}>
          <div className={style.app_row}>
            <div className={style.app_form}>
              <Input
                theme='secondary'
                type='text'
                label='Название чемпионата:'
                className={style.app__input_title}
                name='title'
                value={formik.values.title}
                onGx-input={formik.handleChange}
                error={formik.errors.title}
              />

              <Title type='h2' theme='h4' className={style.app__block}>
                Проведение чемпионата
              </Title>

              <div className={`${style.app_col5_row} ${style.app__block}`}>
                <CountrySelect
                  label='Страна:'
                  theme='secondary'
                  valueType='title'
                  name='country'
                  className={style.app_col5__small}
                  value={formik.values.country}
                  onGx-change={formik.handleChange}
                  error={formik.errors.country}
                />
                {formik.values.country === Russian
                  ? (
                    <StateSelect
                      theme='secondary'
                      className={style.app_col5__big}
                      name='region'
                      value={formik.values.region}
                      onGx-change={formik.handleChange}
                      error={formik.errors.region}
                    />
                    )
                  : (
                    <Input
                      theme='secondary'
                      type='text'
                      label='Область/край:'
                      className={style.app_col5__big}
                      name='region'
                      value={formik.values.region}
                      onGx-input={formik.handleChange}
                      error={formik.errors.region}
                    />
                    )}

              </div>

              <Input
                theme='secondary'
                type='text'
                label='Населенный пункт:'
                className={style.app__block}
                name='locality'
                value={formik.values.locality}
                onGx-input={formik.handleChange}
                error={formik.errors.locality}
              />
              <Input
                theme='secondary'
                type='text'
                label='Наименование учебного заведения:'
                className={style.app__block}
                name='school_name'
                value={formik.values.school_name}
                onGx-input={formik.handleChange}
                error={formik.errors.school_name}
              />

              <div className={`${style.app_col5_row} ${style.app__block}`}>
                <Input
                  theme='secondary'
                  type='tel'
                  label='Телефон:'
                  placeholder='+7'
                  className={style.app_col5__small}
                  name='phone'
                  value={formik.values.phone}
                  onGx-input={formik.handleChange}
                  error={formik.errors.phone}
                />
                <Input
                  theme='secondary'
                  type='email'
                  label='Почта:'
                  className={style.app_col5__big}
                  name='email'
                  value={formik.values.email}
                  onGx-input={formik.handleChange}
                  error={formik.errors.email}
                />
              </div>

              <Checkbox
                theme='secondary'
                name='show_code'
                onGx-change={handleCheckbox}
                value='1'
                checked={formik.values.show_code === '1'}
              >
                Отображать Код доступа на сайте
              </Checkbox>
            </div>
            <div className={style.app_params}>
              <Title type='h2' theme='h3' className={style.app__block}>
                Параметры игры
              </Title>

              <SelectGame
                theme='gray'
                className={style.app__block}
                onGx-change={formik.handleChange}
                name='jeka_version'
                value={formik.values.jeka_version}
                error={formik.errors.jeka_version}
              />

              <div className={style.app_col5_row}>
                <SelectAge
                  className={style.app_col__half}
                  label='Возрастная группа:'
                  onGx-change={formik.handleChange}
                  name='age'
                  value={formik.values.age}
                  error={formik.errors.age}
                />

                <Select
                  label='Время проведения чемпионата:'
                  className={style.app_col__half}
                  onGx-change={formik.handleChange}
                  name='end_time'
                  value={formik.values.end_time}
                  error={formik.errors.end_time}
                >
                  <SelectItem value='1'>1 день</SelectItem>
                  <SelectItem value='3'>3 дня</SelectItem>
                  <SelectItem value='7'>7 дней</SelectItem>
                  <SelectItem value='14'>14 дней</SelectItem>
                  <SelectItem value='30'>1 месяц</SelectItem>
                </Select>
              </div>
            </div>
            <div>
              {formik?.errors?.apiError === ''
                ? null
                : (
                  <ErrorAllText text={formik.errors.apiError} />
                  )}
            </div>
            <Button
              type='submit'
              size='extralarge'
              wide
              highlight
              disabled={formik.isSubmitting}
              className={style.app__submit}
            >
              {formik.isSubmitting ? <Spinner /> : 'Отправить'}
            </Button>
          </div>
        </GxForm>
      </Section>
    </Container>
  )
}

export default ChampionshipApplication
